/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useRef } from "react";
import { Box, FormControl, Skeleton, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { StyledSwitch } from "../total_sales_card/styles";
import { StyledMenuItem } from "../styled_menu_item";
import { SelectedMenuItem } from "../selected_menu_item";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import DropdownSearchBar from "../dorpdown_searchbar";
import {
  ALL_AUDIENCE_MODE,
  AUDIENCE_MODE_KEY,
  AUDIENCE_MODE_OPTIONS,
  BENEFIT_CARD_IDS_KEY,
  CARD_AUDIENCE_MODE,
  CONFIGURABLE_RULE_KEYS,
  derivePartnershipSides,
  describeRuleConstraint,
  EXCEPTION_GUEST_IDS_KEY,
  EXCEPTIONS_PERMITTED_KEY,
  isRuleEditable,
  JOB_ROLE_AUDIENCE_MODE,
  LOCKED_RESTRICTIVE_OPTIONS,
  NO_ACTIVE_PROVIDER_POLICY_TEXT,
  OVERRIDE_MODE_OPTIONS,
  PARTNER_PROGRAM_CHECKING_TEXT,
  PARTNER_PROGRAM_CLEAR_TEXT,
  PARTNERSHIP_HELPER_TEXT,
  parseEntityKey,
  POLICY_LAYER_OPTIONS,
  PROGRAM_END_DATE_KEY,
  PROGRAM_START_DATE_KEY,
  PROGRAM_TIME_WINDOW_KEY,
  PROGRAM_VALID_DAYS_KEY,
  PROGRAM_WEEKDAY_OPTIONS,
  PROVIDER_POLICY_LAYER,
  RECIPIENT_POLICY_LAYER,
  RESTAURANT_AUDIENCE_MODE,
  ruleContract,
  USAGE_FREQUENCY_KEY,
  buildEntityKey,
} from "./partner_program_configuration";
import { sanitizeIntegerInput } from "./policy_configuration";
import {
  ActivePolicyCheckText,
  ActivityChip,
  ActivityChipCheckbox,
  dropdownMenuProps,
  ErrorText,
  EstimatedAudienceLabel,
  EstimatedAudienceValue,
  FieldContainer,
  FieldLabel,
  HelperText,
  PartnershipOptionName,
  PartnershipOptionPair,
  ProgramCard,
  ProgramCardHeader,
  programDatePickerStyles,
  ProgramDivider,
  ProgramFieldPrefix,
  ProgramFieldRow,
  ProgramInnerCard,
  ProgramRangeLabel,
  ProgramRangeRow,
  ProgramRuleBlock,
  ProgramRuleControls,
  ProgramRuleRow,
  ProgramSelect,
  ProgramTimeRow,
  programControlHeight,
  RequiredMark,
  RuleIconWrapper,
  RuleInfoIcon,
  RuleLabel,
  RuleLabelContainer,
  SectionContainer,
  SectionHelperText,
  SectionTitle,
  selectMenuProps,
  SelectPlaceholder,
  StyledSelect,
  StyledTextField,
  ToggleValueText,
  WeekdayChipGroup,
} from "./styles";

const NUMBER_FIELD_WIDTH = { width: "96px", ...programControlHeight };
const LOCK_SELECT_WIDTH = { width: "150px" };
const DATE_FIELD_WIDTH = { width: "170px" };
const TIME_FIELD_WIDTH = { width: "120px", ...programControlHeight };
const AUDIENCE_SELECT_WIDTH = { flex: 1, minWidth: "220px" };
const AUDIENCE_MODE_WIDTH = { width: "240px" };
const EXCEPTION_SELECT_WIDTH = { width: "260px" };

// The one place a date crosses between the form and the payload. State holds the
// ISO day; the picker takes and gives a dayjs, and displays MM/DD/YYYY.
const PROGRAM_DATE_FORMAT = "MM/DD/YYYY";
const ISO_DATE_FORMAT = "YYYY-MM-DD";

const toDatePickerValue = (isoDate) => (isoDate ? dayjs(isoDate, ISO_DATE_FORMAT) : null);

const fromDatePickerValue = (date) => (date?.isValid?.() ? date.format(ISO_DATE_FORMAT) : "");

/**
 * The Partner Benefit Program form. Rendered in place of the group / restaurant form
 * when its tab is selected, so the two share only the draft toggle and the policy name.
 */
export const PartnerBenefitProgramForm = ({
  policyConfigurationData,
  partnerProgramData,
  setPartnerProgramData,
  errors,
  setErrors,
  isEditMode = false,
  activePolicyCheck = {},
  prefillStatus = {},
  partnershipDropdown = {},
  benefitCardsDropdown = {},
  exceptionUsersDropdown = {},
  audienceRestaurantsDropdown = {},
  audienceCardsDropdown = {},
  audienceJobsDropdown = {},
  audiencePreview = {},
}) => {
  const policyLayer = partnerProgramData?.policy_layer || "";
  const isProviderLayer = policyLayer === PROVIDER_POLICY_LAYER;
  const isRecipientLayer = policyLayer === RECIPIENT_POLICY_LAYER;
  const partnershipSides = derivePartnershipSides(partnerProgramData?.partnership);
  const providerSide = parseEntityKey(partnerProgramData?.provider_key);
  const recipientSide = parseEntityKey(partnerProgramData?.recipient_key);
  const hasProgramSides = Boolean(providerSide && recipientSide);
  const audienceMode = partnerProgramData?.audience_mode || ALL_AUDIENCE_MODE;
  const isDraft = Boolean(policyConfigurationData?.draft);
  // A draft occupies no scope, so it does not run the check; a recipient always needs
  // its prefill, draft or not — the contract is what the whole form is authored against.
  // An edit runs no check: the active policy occupying the pair IS the one being
  // edited, so the endpoint would report a conflict against itself.
  const isActivePolicyCheckRequired = !isDraft && !isEditMode;
  const isPrefillReady = !isRecipientLayer || Boolean(partnerProgramData?.prefill);
  const areRulesDisabled = !hasProgramSides
    || !isPrefillReady
    || (isActivePolicyCheckRequired && !activePolicyCheck?.isClear);

  const updateField = (field, value) => {
    setPartnerProgramData((currentData) => ({ ...currentData, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  const updateOverrideMode = (ruleKey, mode) => {
    setPartnerProgramData((currentData) => ({
      ...currentData,
      override_modes: { ...currentData?.override_modes, [ruleKey]: mode },
    }));
  };

  const updateOverrideConstraint = (field, value) => {
    setPartnerProgramData((currentData) => ({
      ...currentData,
      override_constraints: { ...currentData?.override_constraints, [field]: value },
    }));
  };

  const recipientKey = partnerProgramData?.recipient_key || "";
  // The mount-time key, so the clear below fires on an actual CHANGE rather than on
  // every mount — an edit opens with the pair already seeded, and clearing there would
  // wipe the cards, excepted users and audience just loaded. On create the mount value
  // is "", so nothing changes for that flow.
  const previousRecipientKeyRef = useRef(recipientKey);

  // Everything below the pair is scoped to the recipient — cards, guests, restaurants,
  // roles — so a selection made under a previous recipient names entities the endpoint
  // would reject. Cleared on the move rather than filtered on submit.
  //
  // The dimension the author chose survives the move on every program shape; only its
  // selection, which named the previous recipient's restaurants or cards, is dropped.
  useEffect(() => {
    if (previousRecipientKeyRef.current === recipientKey) {
      return;
    }

    previousRecipientKeyRef.current = recipientKey;

    setPartnerProgramData((currentData) => {
      const isSettled = !currentData?.benefit_card_ids?.length
        && !currentData?.exception_network_guest_ids?.length
        && !currentData?.audience_values?.length;

      return isSettled ? currentData : {
        ...currentData,
        benefit_card_ids: [],
        exception_network_guest_ids: [],
        audience_values: [],
      };
    });
  }, [recipientKey, setPartnerProgramData]);

  const handlePolicyLayerChange = (event) => {
    const nextLayer = event.target.value;

    // The prefill belongs to the recipient flow; carrying it into a provider policy
    // would gate provider controls on a contract that does not govern them.
    setPartnerProgramData((currentData) => ({ ...currentData, policy_layer: nextLayer, prefill: null }));
    setErrors((currentErrors) => ({ ...currentErrors, policy_layer: undefined }));
  };

  // `active_partners_list` searches on relationship_name alone, so the name has to be on
  // the row — the pair below it does not say which partnership a search matched.
  const renderPartnershipOption = (option) => (
    <Box sx={{ minWidth: 0 }}>
      <PartnershipOptionName>{option?.relationship_name}</PartnershipOptionName>
      <PartnershipOptionPair>{option?.label}</PartnershipOptionPair>
    </Box>
  );

  // The two sides come off the selected row, so switching partnership has to drop the
  // pair with it — and a group-to-restaurant partnership sets both sides outright,
  // since the program is only ever authored with the group providing.
  const handlePartnershipChange = (event) => {
    const partnershipId = event.target.value;
    const partnership = partnershipDropdown?.options?.find((option) => option?.id === partnershipId)
      || partnershipDropdown?.optionsCache?.find((option) => option?.id === partnershipId)
      || null;
    const sides = derivePartnershipSides(partnership);

    setPartnerProgramData((currentData) => ({
      ...currentData,
      partnership_id: partnershipId,
      partnership,
      provider_key: buildEntityKey(sides.pinnedProvider?.entity_type, sides.pinnedProvider?.entity_id),
      recipient_key: buildEntityKey(sides.pinnedRecipient?.entity_type, sides.pinnedRecipient?.entity_id),
      prefill: null,
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      partnership_id: undefined,
      provider_key: undefined,
      recipient_key: undefined,
    }));
  };

  // A partnership is not directional, so on a group-to-group pair choosing one side as
  // the provider settles the other — there are only two.
  const handleProviderChange = (event) => {
    const providerKey = event.target.value;
    const other = partnershipSides.entities.find(
      (entity) => buildEntityKey(entity.entity_type, entity.entity_id) !== providerKey
    );

    setPartnerProgramData((currentData) => ({
      ...currentData,
      provider_key: providerKey,
      recipient_key: buildEntityKey(other?.entity_type, other?.entity_id),
      prefill: null,
    }));
    setErrors((currentErrors) => ({ ...currentErrors, provider_key: undefined, recipient_key: undefined }));
  };

  // Switching exceptions off drops the selection: the pair is coupled in both
  // directions, so a list left behind without the switch is rejected on submit.
  const handleExceptionsPermittedChange = (isPermitted) => {
    setPartnerProgramData((currentData) => ({
      ...currentData,
      exceptions_permitted: isPermitted,
      exception_network_guest_ids: isPermitted ? currentData?.exception_network_guest_ids || [] : [],
    }));
    setErrors((currentErrors) => ({ ...currentErrors, exception_network_guest_ids: undefined }));
  };

  // A selection belongs to the dimension it was made on — a restaurant id is not a card
  // id — so changing the mode drops it.
  const handleAudienceModeChange = (event) => {
    setPartnerProgramData((currentData) => ({
      ...currentData,
      audience_mode: event.target.value,
      audience_values: [],
    }));
    setErrors((currentErrors) => ({ ...currentErrors, audience_values: undefined }));
  };

  const toggleWeekday = (field, weekday, source) => {
    const current = source || [];
    const next = current.includes(weekday)
      ? current.filter((day) => day !== weekday)
      : [...current, weekday];

    if (field === PROGRAM_VALID_DAYS_KEY) {
      updateField("program_valid_days", next);
      return;
    }

    updateOverrideConstraint("program_valid_days", next);
  };

  const entityLabel = (entityKey) => {
    const entity = partnershipSides.entities.find(
      (side) => buildEntityKey(side.entity_type, side.entity_id) === entityKey
    );

    return entity?.entity_name || "";
  };

  const findOptionName = (dropdown, optionId) => {
    const matches = (option) => String(option?.id) === String(optionId);
    const option = dropdown?.optionsCache?.find(matches) || dropdown?.options?.find(matches);

    return option?.name;
  };

  // One selection reads as its own name; several collapse to a count, since a joined
  // list overflows the field long before it stays legible.
  const describeSelection = (dropdown, selectedIds) => {
    const ids = selectedIds || [];

    if (ids.length > 1) {
      return `${ids.length} selected`;
    }

    return ids.length ? findOptionName(dropdown, ids[0]) || `${ids[0]}` : "";
  };

  // A bound that is a list of ids reads as nothing to a person, so name every entry the
  // dropdown has already loaded and leave the rest as their id.
  const describeIdBound = (dropdown, ids) => (
    Array.isArray(ids) && ids.length
      ? ids.map((id) => findOptionName(dropdown, id) || `${id}`).join(", ")
      : null
  );

  const overrideModeFor = (ruleKey) => partnerProgramData?.override_modes?.[ruleKey] || "locked";
  const isConfigurable = (ruleKey) => isProviderLayer && overrideModeFor(ruleKey) === "configurable";

  /** The envelope's own message: a range reported only in the snackbar names no control. */
  const renderRangeError = (field) => (
    errors?.[field] ? <ErrorText data-testid={`policy-engine-${field}-error`}>{errors[field]}</ErrorText> : null
  );

  /**
   * The lock control a provider states a rule under. A recipient authors none of it,
   * so the same slot carries the info icon describing what the provider allowed.
   */
  const renderOverrideControl = (ruleKey) => {
    if (!isProviderLayer) {
      return null;
    }

    const options = CONFIGURABLE_RULE_KEYS.includes(ruleKey)
      ? OVERRIDE_MODE_OPTIONS
      : LOCKED_RESTRICTIVE_OPTIONS;

    return (
      <FormControl size="small" sx={LOCK_SELECT_WIDTH}>
        <ProgramSelect
          data-testid={`policy-engine-override-mode-${ruleKey}`}
          size="small"
          disabled={areRulesDisabled}
          value={overrideModeFor(ruleKey)}
          // The tick belongs in the menu, not in the closed field — MUI would
          // otherwise clone the selected item, SelectedMenuItem's icon and all.
          renderValue={(selected) => options.find((option) => option.value === selected)?.label}
          MenuProps={selectMenuProps()}
          onChange={(event) => updateOverrideMode(ruleKey, event.target.value)}
        >
          {options.map((option) => (
            <StyledMenuItem key={option.value} value={option.value}>
              {overrideModeFor(ruleKey) === option.value
                ? <SelectedMenuItem data={option.label} />
                : option.label}
            </StyledMenuItem>
          ))}
        </ProgramSelect>
      </FormControl>
    );
  };

  /** Recipient only: what the provider's terms say about the rule this label names. */
  const renderConstraintInfo = (ruleKey, boundLabel = null) => {
    if (!isRecipientLayer) {
      return null;
    }

    const contract = ruleContract(partnerProgramData, ruleKey);
    const description = describeRuleConstraint(contract, ruleKey, boundLabel);

    if (!description) {
      return null;
    }

    return (
      <Tooltip title={description} arrow>
        <RuleInfoIcon data-testid={`policy-engine-rule-info-${ruleKey}`}>
          <InfoOutlinedIcon />
        </RuleInfoIcon>
      </Tooltip>
    );
  };

  const ruleDisabled = (ruleKey) => areRulesDisabled || !isRuleEditable(partnerProgramData, ruleKey);

  const audienceOption = AUDIENCE_MODE_OPTIONS.find((option) => option.value === audienceMode);

  const audienceDropdownFor = (mode) => {
    if (mode === RESTAURANT_AUDIENCE_MODE) {
      return audienceRestaurantsDropdown;
    }

    return mode === CARD_AUDIENCE_MODE ? audienceCardsDropdown : audienceJobsDropdown;
  };

  const renderAudienceSelection = () => {
    if (audienceMode === ALL_AUDIENCE_MODE) {
      return null;
    }

    const dropdown = audienceDropdownFor(audienceMode);
    const searchPlaceholder = audienceMode === JOB_ROLE_AUDIENCE_MODE
      ? "Search job title"
      : (audienceMode === CARD_AUDIENCE_MODE ? "Search perq card" : "Search restaurant");

    return (
      <Box sx={AUDIENCE_SELECT_WIDTH}>
        <FieldLabel>
          {audienceOption?.selectionLabel}
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <FormControl fullWidth size="small">
          <ProgramSelect
            data-testid="policy-engine-audience-values-select"
            size="small"
            multiple
            displayEmpty
            disabled={areRulesDisabled}
            value={partnerProgramData?.audience_values || []}
            renderValue={() => {
              const label = describeSelection(dropdown, partnerProgramData?.audience_values);
              return label || <SelectPlaceholder>Select {audienceOption?.selectionLabel?.toLowerCase()}</SelectPlaceholder>;
            }}
            onChange={(event) => updateField("audience_values", event.target.value)}
            onClose={dropdown?.onMenuClose}
            error={!!errors?.audience_values}
            MenuProps={dropdownMenuProps(dropdown?.menuRef)}
          >
            <DropdownSearchBar
              changeSearchText={dropdown?.onSearchChange}
              placeholder={searchPlaceholder}
              isFetching={dropdown?.isFetching}
            />
            {dropdown?.options?.map((option) => (
              <StyledMenuItem key={option?.id} value={option?.id}>
                {partnerProgramData?.audience_values?.includes(option?.id)
                  ? <SelectedMenuItem data={option?.name} />
                  : option?.name}
              </StyledMenuItem>
            ))}
            <DropdownInfiniteLoader
              hasNextPage={dropdown?.hasNextPage}
              targetRef={dropdown?.loaderRef}
            />
          </ProgramSelect>
        </FormControl>
        {errors?.audience_values && <ErrorText>{errors.audience_values}</ErrorText>}
      </Box>
    );
  };

  const layerHelperText = (POLICY_LAYER_OPTIONS.find((option) => option.value === policyLayer)
    || POLICY_LAYER_OPTIONS[0]).helperText;

  /** A program date: the reports page's picker, restyled to this form's outline. */
  const renderProgramDate = (ruleKey, field, testId) => (
    <DatePicker
      format={PROGRAM_DATE_FORMAT}
      disabled={ruleDisabled(ruleKey)}
      value={toDatePickerValue(partnerProgramData?.[field])}
      onChange={(date) => updateField(field, fromDatePickerValue(date))}
      sx={{ ...programDatePickerStyles, ...DATE_FIELD_WIDTH }}
      slotProps={{
        textField: {
          size: "small",
          error: !!errors?.[field],
          inputProps: { "data-testid": testId },
        },
      }}
    />
  );

  return (
    <>
      <SectionContainer>
        <FieldContainer>
          <FieldLabel>Policy layer<RequiredMark>*</RequiredMark></FieldLabel>
          <FormControl fullWidth size="small">
            <ProgramSelect
              data-testid="policy-engine-policy-layer-select"
              size="small"
              displayEmpty
              disabled={isEditMode}
              value={policyLayer}
              renderValue={(selected) => {
                const option = POLICY_LAYER_OPTIONS.find((item) => item.value === selected);
                return option?.label || <SelectPlaceholder>Select type</SelectPlaceholder>;
              }}
              MenuProps={selectMenuProps()}
              onChange={handlePolicyLayerChange}
              error={!!errors?.policy_layer}
            >
              {POLICY_LAYER_OPTIONS.map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  {policyLayer === option.value ? <SelectedMenuItem data={option.label} /> : option.label}
                </StyledMenuItem>
              ))}
            </ProgramSelect>
          </FormControl>
          {errors?.policy_layer ? <ErrorText>{errors.policy_layer}</ErrorText> : <HelperText>{layerHelperText}</HelperText>}
        </FieldContainer>

        {/* Not rendered while editing: the pair is the program's identity and cannot
            move, so the partnership behind it has nothing left to choose. */}
        {!isEditMode && (
        <FieldContainer>
          <FieldLabel>Partnership<RequiredMark>*</RequiredMark></FieldLabel>
          <FormControl fullWidth size="small">
            <ProgramSelect
              data-testid="policy-engine-partnership-select"
              size="small"
              displayEmpty
              value={partnerProgramData?.partnership_id || ""}
              renderValue={() => (
                partnerProgramData?.partnership?.label
                || <SelectPlaceholder>Select a partnership</SelectPlaceholder>
              )}
              onChange={handlePartnershipChange}
              onClose={partnershipDropdown?.onMenuClose}
              error={!!errors?.partnership_id}
              MenuProps={dropdownMenuProps(partnershipDropdown?.menuRef)}
            >
              <DropdownSearchBar
                changeSearchText={partnershipDropdown?.onSearchChange}
                placeholder="Search partnership"
                isFetching={partnershipDropdown?.isFetching}
              />
              {partnershipDropdown?.options?.map((option) => (
                <StyledMenuItem key={option?.id} value={option?.id}>
                  {partnerProgramData?.partnership_id === option?.id
                    ? <SelectedMenuItem data={renderPartnershipOption(option)} />
                    : renderPartnershipOption(option)}
                </StyledMenuItem>
              ))}
              <DropdownInfiniteLoader
                hasNextPage={partnershipDropdown?.hasNextPage}
                targetRef={partnershipDropdown?.loaderRef}
              />
            </ProgramSelect>
          </FormControl>
          {errors?.partnership_id
            ? <ErrorText>{errors.partnership_id}</ErrorText>
            : <HelperText>{PARTNERSHIP_HELPER_TEXT}</HelperText>}
        </FieldContainer>
        )}

        {/* Both stay closed until a partnership names the two sides they choose
            between. A group-to-restaurant pair fixes both: the program is DIRECTED,
            so a restaurant may only ever be the recipient. */}
        <ProgramFieldRow>
          <FieldContainer>
            <FieldLabel>Provider<RequiredMark>*</RequiredMark></FieldLabel>
            <FormControl fullWidth size="small">
              <ProgramSelect
                data-testid="policy-engine-program-provider-select"
                size="small"
                displayEmpty
                disabled={isEditMode || !partnershipSides.isGroupToGroup}
                value={partnerProgramData?.provider_key || ""}
                renderValue={(selected) => (
                  entityLabel(selected) || <SelectPlaceholder>Select a provider</SelectPlaceholder>
                )}
                MenuProps={selectMenuProps()}
                onChange={handleProviderChange}
                error={!!errors?.provider_key}
              >
                {partnershipSides.entities.map((entity) => {
                  const entityKey = buildEntityKey(entity.entity_type, entity.entity_id);

                  return (
                    <StyledMenuItem key={entityKey} value={entityKey}>
                      {partnerProgramData?.provider_key === entityKey
                        ? <SelectedMenuItem data={entity.entity_name} />
                        : entity.entity_name}
                    </StyledMenuItem>
                  );
                })}
              </ProgramSelect>
            </FormControl>
            {!!errors?.provider_key && <ErrorText>{errors.provider_key}</ErrorText>}
          </FieldContainer>

          <FieldContainer>
            <FieldLabel>Recipient</FieldLabel>
            <FormControl fullWidth size="small">
              <ProgramSelect
                data-testid="policy-engine-program-recipient-select"
                size="small"
                displayEmpty
                disabled
                value={partnerProgramData?.recipient_key || ""}
                renderValue={(selected) => (
                  entityLabel(selected) || <SelectPlaceholder>Select a recipient</SelectPlaceholder>
                )}
              >
                {partnershipSides.entities.map((entity) => {
                  const entityKey = buildEntityKey(entity.entity_type, entity.entity_id);

                  return (
                    <StyledMenuItem key={entityKey} value={entityKey}>{entity.entity_name}</StyledMenuItem>
                  );
                })}
              </ProgramSelect>
            </FormControl>
            {!!errors?.recipient_key && <ErrorText>{errors.recipient_key}</ErrorText>}
          </FieldContainer>
        </ProgramFieldRow>

        {/* A group-to-restaurant partnership pins both sides, so the pair can be
            complete with no layer — and nothing else runs until one is chosen. */}
        {hasProgramSides && !policyLayer && (
          <ErrorText data-testid="policy-engine-program-layer-required">
            Select a policy layer to continue.
          </ErrorText>
        )}

        {/* Outcome of the check on the directed pair. A conflict is a 422 and is
            reported in the snackbar by the page, so only these states show here. */}
        {isActivePolicyCheckRequired && hasProgramSides && !!activePolicyCheck?.isChecking && (
          <ActivePolicyCheckText data-testid="policy-engine-program-check-checking">
            {PARTNER_PROGRAM_CHECKING_TEXT}
          </ActivePolicyCheckText>
        )}
        {isActivePolicyCheckRequired && hasProgramSides
          && !activePolicyCheck?.isChecking && !!activePolicyCheck?.isClear && (
          <ActivePolicyCheckText isClear data-testid="policy-engine-program-check-clear">
            {PARTNER_PROGRAM_CLEAR_TEXT}
          </ActivePolicyCheckText>
        )}
        {isRecipientLayer && hasProgramSides && !!prefillStatus?.isMissing && (
          <ErrorText data-testid="policy-engine-program-prefill-missing">
            {NO_ACTIVE_PROVIDER_POLICY_TEXT}
          </ErrorText>
        )}

        <FieldContainer>
          <FieldLabel>
            Benefit{isProviderLayer && <RequiredMark>*</RequiredMark>}
            {renderConstraintInfo(BENEFIT_CARD_IDS_KEY)}
          </FieldLabel>
          <FormControl fullWidth size="small">
            <ProgramSelect
              data-testid="policy-engine-benefit-cards-select"
              size="small"
              multiple
              displayEmpty
              disabled={ruleDisabled(BENEFIT_CARD_IDS_KEY)}
              value={partnerProgramData?.benefit_card_ids || []}
              renderValue={() => {
                const label = describeSelection(benefitCardsDropdown, partnerProgramData?.benefit_card_ids);
                return label || <SelectPlaceholder>Select a benefit</SelectPlaceholder>;
              }}
              onChange={(event) => updateField("benefit_card_ids", event.target.value)}
              onClose={benefitCardsDropdown?.onMenuClose}
              error={!!errors?.benefit_card_ids}
              MenuProps={dropdownMenuProps(benefitCardsDropdown?.menuRef)}
            >
              <DropdownSearchBar
                changeSearchText={benefitCardsDropdown?.onSearchChange}
                placeholder="Search perq card"
                isFetching={benefitCardsDropdown?.isFetching}
              />
              {benefitCardsDropdown?.options?.map((option) => (
                <StyledMenuItem key={option?.id} value={option?.id}>
                  {partnerProgramData?.benefit_card_ids?.includes(option?.id)
                    ? <SelectedMenuItem data={option?.name} />
                    : option?.name}
                </StyledMenuItem>
              ))}
              <DropdownInfiniteLoader
                hasNextPage={benefitCardsDropdown?.hasNextPage}
                targetRef={benefitCardsDropdown?.loaderRef}
              />
            </ProgramSelect>
          </FormControl>
          {!!errors?.benefit_card_ids && <ErrorText>{errors.benefit_card_ids}</ErrorText>}
        </FieldContainer>

        <FieldContainer sx={{ marginBottom: 0 }}>
          <FieldLabel>Description</FieldLabel>
          <StyledTextField
            inputProps={{ "data-testid": "policy-engine-program-description-input" }}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Description of this policy"
            value={partnerProgramData?.description || ""}
            onChange={(event) => updateField("description", event.target.value)}
          />
        </FieldContainer>
      </SectionContainer>

      {/* Effective rules */}
      <SectionContainer>
        <SectionTitle>Effective rules</SectionTitle>

        <ProgramRuleBlock>
          <ProgramRuleRow>
            <RuleLabelContainer>
              <RuleIconWrapper><SwapHorizIcon /></RuleIconWrapper>
              <RuleLabel>Usage frequency{renderConstraintInfo(USAGE_FREQUENCY_KEY)}</RuleLabel>
            </RuleLabelContainer>
            <ProgramRuleControls>
              <ProgramFieldPrefix>Times</ProgramFieldPrefix>
              <StyledTextField
                size="small"
                sx={NUMBER_FIELD_WIDTH}
                placeholder="2"
                disabled={ruleDisabled(USAGE_FREQUENCY_KEY)}
                inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-usage-times-input" }}
                value={partnerProgramData?.usage_number_of_time ?? ""}
                onChange={(event) => updateField("usage_number_of_time", sanitizeIntegerInput(event.target.value))}
                error={!!errors?.usage_frequency}
              />
              <ProgramFieldPrefix>per</ProgramFieldPrefix>
              <StyledTextField
                size="small"
                sx={NUMBER_FIELD_WIDTH}
                placeholder="7"
                disabled={ruleDisabled(USAGE_FREQUENCY_KEY)}
                inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-usage-days-input" }}
                value={partnerProgramData?.usage_number_of_days ?? ""}
                onChange={(event) => updateField("usage_number_of_days", sanitizeIntegerInput(event.target.value))}
                error={!!errors?.usage_frequency}
              />
              <ProgramFieldPrefix>days</ProgramFieldPrefix>
              {renderOverrideControl(USAGE_FREQUENCY_KEY)}
            </ProgramRuleControls>
          </ProgramRuleRow>
          {!!errors?.usage_frequency && <ErrorText>{errors.usage_frequency}</ErrorText>}

          {isConfigurable(USAGE_FREQUENCY_KEY) && (
            <ProgramRangeRow data-testid="policy-engine-usage-frequency-range">
              <ProgramRangeLabel>Set range for how many times / how many days</ProgramRangeLabel>
              <ProgramRuleControls>
                <ProgramFieldPrefix>Times</ProgramFieldPrefix>
                <StyledTextField
                  size="small"
                  sx={NUMBER_FIELD_WIDTH}
                  placeholder="4"
                  disabled={areRulesDisabled}
                  error={!!errors?.usage_frequency_range}
                  inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-usage-times-range-input" }}
                  value={partnerProgramData?.override_constraints?.usage_number_of_time ?? ""}
                  onChange={(event) => updateOverrideConstraint("usage_number_of_time", sanitizeIntegerInput(event.target.value))}
                />
                <ProgramFieldPrefix>per</ProgramFieldPrefix>
                <StyledTextField
                  size="small"
                  sx={NUMBER_FIELD_WIDTH}
                  placeholder="7"
                  disabled={areRulesDisabled}
                  error={!!errors?.usage_frequency_range}
                  inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-usage-days-range-input" }}
                  value={partnerProgramData?.override_constraints?.usage_number_of_days ?? ""}
                  onChange={(event) => updateOverrideConstraint("usage_number_of_days", sanitizeIntegerInput(event.target.value))}
                />
                <ProgramFieldPrefix>days</ProgramFieldPrefix>
              </ProgramRuleControls>
              {renderRangeError("usage_frequency_range")}
            </ProgramRangeRow>
          )}
        </ProgramRuleBlock>

        <ProgramRuleBlock>
          <ProgramRuleRow>
            <RuleLabelContainer>
              <RuleIconWrapper><ErrorOutlineIcon /></RuleIconWrapper>
              <RuleLabel>Exceptions permitted{renderConstraintInfo(EXCEPTIONS_PERMITTED_KEY)}</RuleLabel>
            </RuleLabelContainer>
            <ProgramRuleControls>
              <Box sx={{ alignItems: "center", display: "flex", gap: "4px" }}>
                <StyledSwitch
                  data-testid="policy-engine-program-exceptions-switch"
                  disabled={ruleDisabled(EXCEPTIONS_PERMITTED_KEY)}
                  checked={partnerProgramData?.exceptions_permitted === true}
                  onChange={(event) => handleExceptionsPermittedChange(event.target.checked)}
                />
                <ToggleValueText>
                  {partnerProgramData?.exceptions_permitted === true ? "Allowed" : "Not allowed"}
                </ToggleValueText>
              </Box>
              {renderOverrideControl(EXCEPTIONS_PERMITTED_KEY)}
            </ProgramRuleControls>
          </ProgramRuleRow>
        </ProgramRuleBlock>

        <ProgramRuleBlock>
          <ProgramRuleRow>
            <RuleLabelContainer>
              <RuleIconWrapper><PersonOutlineIcon /></RuleIconWrapper>
              <RuleLabel>
                Excepted users{partnerProgramData?.exceptions_permitted && <RequiredMark>*</RequiredMark>}
                {renderConstraintInfo(
                  EXCEPTION_GUEST_IDS_KEY,
                  describeIdBound(
                    exceptionUsersDropdown,
                    ruleContract(partnerProgramData, EXCEPTION_GUEST_IDS_KEY)?.bound
                  )
                )}
              </RuleLabel>
            </RuleLabelContainer>
            <ProgramRuleControls>
              <FormControl size="small" sx={EXCEPTION_SELECT_WIDTH}>
                <StyledSelect
                  data-testid="policy-engine-program-exception-users-select"
                  size="small"
                  multiple
                  displayEmpty
                  disabled={ruleDisabled(EXCEPTION_GUEST_IDS_KEY) || !partnerProgramData?.exceptions_permitted}
                  value={partnerProgramData?.exception_network_guest_ids || []}
                  renderValue={() => {
                    const label = describeSelection(exceptionUsersDropdown, partnerProgramData?.exception_network_guest_ids);
                    return label || <SelectPlaceholder>Select users</SelectPlaceholder>;
                  }}
                  onChange={(event) => updateField("exception_network_guest_ids", event.target.value)}
                  onClose={exceptionUsersDropdown?.onMenuClose}
                  error={!!errors?.exception_network_guest_ids}
                  MenuProps={dropdownMenuProps(exceptionUsersDropdown?.menuRef)}
                >
                  <DropdownSearchBar
                    changeSearchText={exceptionUsersDropdown?.onSearchChange}
                    placeholder="Search user"
                    isFetching={exceptionUsersDropdown?.isFetching}
                  />
                  {exceptionUsersDropdown?.options?.map((option) => (
                    <StyledMenuItem key={option?.id} value={option?.id}>
                      {partnerProgramData?.exception_network_guest_ids?.includes(option?.id)
                        ? <SelectedMenuItem data={option?.name} />
                        : option?.name}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={exceptionUsersDropdown?.hasNextPage}
                    targetRef={exceptionUsersDropdown?.loaderRef}
                  />
                </StyledSelect>
              </FormControl>
            </ProgramRuleControls>
          </ProgramRuleRow>
        </ProgramRuleBlock>

        {/* Audience Assignment — the recipient's alone to decide, so it carries no
            override control on either policy type. */}
        <ProgramCard>
          <ProgramCardHeader>
            <GroupsIcon />
            <SectionTitle>Audience Assignment</SectionTitle>
          </ProgramCardHeader>
          <SectionHelperText sx={{ marginBottom: 0, marginTop: "4px" }}>
            This determines which of its users receive this Partner Benefit.
          </SectionHelperText>

          <ProgramInnerCard>
            <ProgramFieldRow sx={{ alignItems: "flex-start" }}>
              <Box sx={AUDIENCE_MODE_WIDTH}>
                <FieldLabel>
                  Audience Mode<RequiredMark>*</RequiredMark>
                  {renderConstraintInfo(AUDIENCE_MODE_KEY)}
                </FieldLabel>
                <FormControl fullWidth size="small">
                  <ProgramSelect
                    data-testid="policy-engine-audience-mode-select"
                    size="small"
                    disabled={areRulesDisabled}
                    value={audienceMode}
                    renderValue={(selected) => (
                      AUDIENCE_MODE_OPTIONS.find((option) => option.value === selected)?.label
                    )}
                    MenuProps={selectMenuProps("260px")}
                    onChange={handleAudienceModeChange}
                  >
                    {AUDIENCE_MODE_OPTIONS.map((option) => (
                      <StyledMenuItem key={option.value} value={option.value}>
                        {audienceMode === option.value
                          ? <SelectedMenuItem data={option.label} />
                          : option.label}
                      </StyledMenuItem>
                    ))}
                  </ProgramSelect>
                </FormControl>
              </Box>
              {renderAudienceSelection()}
            </ProgramFieldRow>

            <ProgramDivider />

            <EstimatedAudienceLabel>
              Estimated Audience
              <Tooltip
                title="Employees / Network Guests the current audience mode reaches, counted live"
                arrow
              >
                <RuleInfoIcon><InfoOutlinedIcon /></RuleInfoIcon>
              </Tooltip>
            </EstimatedAudienceLabel>
            {audiencePreview?.isFetching ? (
              <Skeleton
                variant="rectangular"
                data-testid="policy-engine-audience-preview-loader"
                sx={{ borderRadius: "6px", height: "30px", marginTop: "6px", width: "72px" }}
              />
            ) : (
              <EstimatedAudienceValue data-testid="policy-engine-audience-preview-count">
                {audiencePreview?.count ?? 0}
              </EstimatedAudienceValue>
            )}
          </ProgramInnerCard>
        </ProgramCard>

        {/* Schedule & Restrictions */}
        <ProgramCard>
          <ProgramCardHeader>
            <AccessTimeIcon />
            <SectionTitle>Schedule &amp; Restrictions</SectionTitle>
          </ProgramCardHeader>
          <SectionHelperText sx={{ marginBottom: 0, marginTop: "4px" }}>
            Determines when the partner benefit programs can be used
          </SectionHelperText>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ProgramRuleBlock>
              <ProgramRuleRow>
                <RuleLabelContainer>
                  <RuleLabel>Program start date{renderConstraintInfo(PROGRAM_START_DATE_KEY)}</RuleLabel>
                </RuleLabelContainer>
                <ProgramRuleControls>
                  {renderProgramDate(
                    PROGRAM_START_DATE_KEY, "program_start_date", "policy-engine-program-start-date-input"
                  )}
                  {renderOverrideControl(PROGRAM_START_DATE_KEY)}
                </ProgramRuleControls>
              </ProgramRuleRow>
              {!!errors?.program_start_date && <ErrorText>{errors.program_start_date}</ErrorText>}

              {isConfigurable(PROGRAM_START_DATE_KEY) && (
                <ProgramRangeRow data-testid="policy-engine-program-start-date-range">
                  <ProgramRangeLabel>Set range — earliest start the recipient may choose</ProgramRangeLabel>
                  <DatePicker
                    format={PROGRAM_DATE_FORMAT}
                    disabled={areRulesDisabled}
                    value={toDatePickerValue(partnerProgramData?.override_constraints?.program_start_date)}
                    onChange={(date) => updateOverrideConstraint("program_start_date", fromDatePickerValue(date))}
                    sx={{ ...programDatePickerStyles, ...DATE_FIELD_WIDTH }}
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: { "data-testid": "policy-engine-program-start-date-range-input" },
                        error: !!errors?.program_start_date_range,
                      },
                    }}
                  />
                  {renderRangeError("program_start_date_range")}
                </ProgramRangeRow>
              )}
            </ProgramRuleBlock>

            <ProgramRuleBlock>
              <ProgramRuleRow>
                <RuleLabelContainer>
                  <RuleLabel>Program end date{renderConstraintInfo(PROGRAM_END_DATE_KEY)}</RuleLabel>
                </RuleLabelContainer>
                <ProgramRuleControls>
                  {renderProgramDate(
                    PROGRAM_END_DATE_KEY, "program_end_date", "policy-engine-program-end-date-input"
                  )}
                  {renderOverrideControl(PROGRAM_END_DATE_KEY)}
                </ProgramRuleControls>
              </ProgramRuleRow>
              {!!errors?.program_end_date && <ErrorText>{errors.program_end_date}</ErrorText>}

              {isConfigurable(PROGRAM_END_DATE_KEY) && (
                <ProgramRangeRow data-testid="policy-engine-program-end-date-range">
                  <ProgramRangeLabel>Set range — latest end the recipient may choose</ProgramRangeLabel>
                  <DatePicker
                    format={PROGRAM_DATE_FORMAT}
                    disabled={areRulesDisabled}
                    value={toDatePickerValue(partnerProgramData?.override_constraints?.program_end_date)}
                    onChange={(date) => updateOverrideConstraint("program_end_date", fromDatePickerValue(date))}
                    sx={{ ...programDatePickerStyles, ...DATE_FIELD_WIDTH }}
                    slotProps={{
                      textField: {
                        size: "small",
                        inputProps: { "data-testid": "policy-engine-program-end-date-range-input" },
                        error: !!errors?.program_end_date_range,
                      },
                    }}
                  />
                  {renderRangeError("program_end_date_range")}
                </ProgramRangeRow>
              )}
            </ProgramRuleBlock>
          </LocalizationProvider>

          <ProgramRuleBlock>
            <ProgramRuleRow>
              <RuleLabelContainer>
                <RuleLabel>Valid days{renderConstraintInfo(PROGRAM_VALID_DAYS_KEY)}</RuleLabel>
              </RuleLabelContainer>
              <ProgramRuleControls>
                <WeekdayChipGroup>
                  {PROGRAM_WEEKDAY_OPTIONS.map((option) => {
                    const isSelected = partnerProgramData?.program_valid_days?.includes(option.value);
                    const isDisabled = ruleDisabled(PROGRAM_VALID_DAYS_KEY);

                    return (
                      <ActivityChip
                        key={option.value}
                        data-testid={`policy-engine-valid-day-${option.value}`}
                        isSelected={isSelected}
                        sx={isDisabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                        onClick={() => !isDisabled
                          && toggleWeekday(PROGRAM_VALID_DAYS_KEY, option.value, partnerProgramData?.program_valid_days)}
                      >
                        <ActivityChipCheckbox
                          checked={Boolean(isSelected)}
                          disabled={isDisabled}
                          disableRipple
                          tabIndex={-1}
                          inputProps={{ "aria-label": option.label }}
                        />
                        {option.label}
                      </ActivityChip>
                    );
                  })}
                </WeekdayChipGroup>
                {renderOverrideControl(PROGRAM_VALID_DAYS_KEY)}
              </ProgramRuleControls>
            </ProgramRuleRow>
            {!!errors?.program_valid_days && <ErrorText>{errors.program_valid_days}</ErrorText>}

            {isConfigurable(PROGRAM_VALID_DAYS_KEY) && (
              <ProgramRangeRow data-testid="policy-engine-program-valid-days-range">
                <ProgramRangeLabel>Set range — days the recipient may choose from</ProgramRangeLabel>
                <WeekdayChipGroup>
                  {PROGRAM_WEEKDAY_OPTIONS.map((option) => {
                    const isSelected = partnerProgramData?.override_constraints?.program_valid_days?.includes(option.value);

                    return (
                      <ActivityChip
                        key={option.value}
                        data-testid={`policy-engine-valid-day-range-${option.value}`}
                        isSelected={isSelected}
                        sx={areRulesDisabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                        onClick={() => !areRulesDisabled && toggleWeekday(
                          "override_constraints",
                          option.value,
                          partnerProgramData?.override_constraints?.program_valid_days
                        )}
                      >
                        <ActivityChipCheckbox
                          checked={Boolean(isSelected)}
                          disabled={areRulesDisabled}
                          disableRipple
                          tabIndex={-1}
                          inputProps={{ "aria-label": option.label }}
                        />
                        {option.label}
                      </ActivityChip>
                    );
                  })}
                </WeekdayChipGroup>
                {renderRangeError("program_valid_days_range")}
              </ProgramRangeRow>
            )}
          </ProgramRuleBlock>

          <ProgramRuleBlock>
            <ProgramRuleRow>
              <RuleLabelContainer>
                <RuleLabel>Valid time window{renderConstraintInfo(PROGRAM_TIME_WINDOW_KEY)}</RuleLabel>
              </RuleLabelContainer>
              <ProgramRuleControls>
                <ProgramTimeRow>
                  <ProgramFieldPrefix>From</ProgramFieldPrefix>
                  <StyledTextField
                    size="small"
                    type="time"
                    sx={TIME_FIELD_WIDTH}
                    disabled={ruleDisabled(PROGRAM_TIME_WINDOW_KEY)}
                    inputProps={{ "data-testid": "policy-engine-program-time-start-input" }}
                    value={partnerProgramData?.program_valid_time_start || ""}
                    onChange={(event) => updateField("program_valid_time_start", event.target.value)}
                    error={!!errors?.program_valid_time_window}
                  />
                  <ProgramFieldPrefix>To</ProgramFieldPrefix>
                  <StyledTextField
                    size="small"
                    type="time"
                    sx={TIME_FIELD_WIDTH}
                    disabled={ruleDisabled(PROGRAM_TIME_WINDOW_KEY)}
                    inputProps={{ "data-testid": "policy-engine-program-time-end-input" }}
                    value={partnerProgramData?.program_valid_time_end || ""}
                    onChange={(event) => updateField("program_valid_time_end", event.target.value)}
                    error={!!errors?.program_valid_time_window}
                  />
                </ProgramTimeRow>
                {renderOverrideControl(PROGRAM_TIME_WINDOW_KEY)}
              </ProgramRuleControls>
            </ProgramRuleRow>
            {!!errors?.program_valid_time_window && <ErrorText>{errors.program_valid_time_window}</ErrorText>}

            {isConfigurable(PROGRAM_TIME_WINDOW_KEY) && (
              <ProgramRangeRow data-testid="policy-engine-program-time-window-range">
                <ProgramRangeLabel>Set range — window the recipient may choose within</ProgramRangeLabel>
                <ProgramTimeRow>
                  <ProgramFieldPrefix>From</ProgramFieldPrefix>
                  <StyledTextField
                    size="small"
                    type="time"
                    sx={TIME_FIELD_WIDTH}
                    disabled={areRulesDisabled}
                    error={!!errors?.program_valid_time_window_range}
                    inputProps={{ "data-testid": "policy-engine-program-time-start-range-input" }}
                    value={partnerProgramData?.override_constraints?.program_valid_time_start || ""}
                    onChange={(event) => updateOverrideConstraint("program_valid_time_start", event.target.value)}
                  />
                  <ProgramFieldPrefix>To</ProgramFieldPrefix>
                  <StyledTextField
                    size="small"
                    type="time"
                    sx={TIME_FIELD_WIDTH}
                    disabled={areRulesDisabled}
                    error={!!errors?.program_valid_time_window_range}
                    inputProps={{ "data-testid": "policy-engine-program-time-end-range-input" }}
                    value={partnerProgramData?.override_constraints?.program_valid_time_end || ""}
                    onChange={(event) => updateOverrideConstraint("program_valid_time_end", event.target.value)}
                  />
                </ProgramTimeRow>
                {renderRangeError("program_valid_time_window_range")}
              </ProgramRangeRow>
            )}
          </ProgramRuleBlock>
        </ProgramCard>
      </SectionContainer>
    </>
  );
};
